import OrganizationsStore from '../../../../../stores/observables/OrganizationsStore';

/**
 * Whether the PSPDFKit viewer is being accessed from a custom domain, which
 * is a domain other than the default that's granted by the organization's
 * vendor. This is necessary because if the viewer is accessed from a custom
 * domain (e.g. a vanity url), then a custom license key for that organization
 * must be used. That custom license key is tied to the org's custom domain.
 */
export const isCustomDomain = (): boolean => {
  const org = OrganizationsStore.getCurrentOrganization();
  if (!org?.vendor) {
    return false;
  }

  const vendorUrl = new URL(org.vendor.homeUrl);
  return window.location.hostname !== vendorUrl.hostname;
};

/**
 * PSPDFKit requires a license key for each domain/subdomain where PDFs are
 * going to be displayed. Since each organization could have a vanity URL,
 * there are at least 2 possible domains (hence, 2 license keys) where this
 * could happen, generating the following scenarios:
 *  1. The organization doesn't have a vanity URL and no license key: this means
 *     the org doesn't have (yet) a vanity url, therefore, the PDFs will always
 *     be accessed from the default domain, so the default license key is used
 *  2. The organization HAS a vanity URL, but no license key: since the org
 *     has a vanity URL, it means that PDFs can be accessed from the default
 *     domain, but also from the org's custom domain (room's contact view page).
 *     Since the license key is not configured for the vanity url, PSPDFKit won't
 *     work on that domain, therefore, the viewer is turned OFF for this scenario
 *  3. The org HAS a vanity URL and a license key: same as the last one, but
 *     since the org has a license key, that custom license key must be used
 *     based on the domain where the app is being accessed. For the internal
 *     app (always on the default domain), the default license key is used, but
 *     when it's accessed from the custom domain, the org's custom license key
 *     is used; therefore, no errors should happen and the viewer is ON
 */
export const isPSPDFKitViewerEnabled = (
  isNewPdfViewerEnabled: boolean
): boolean => {
  const org = OrganizationsStore.getCurrentOrganization();
  if (!org) {
    return false;
  }

  const hasDefaultLicense = !!org.vendor?.pdfViewerLicenseKey;
  const hasCustomLicense = !!org.pdfViewerLicenseKey;

  return (
    isNewPdfViewerEnabled &&
    ((!isCustomDomain() && hasDefaultLicense) || hasCustomLicense)
  );
};
